import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Check, Loader, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logoColor from '../assets/libben-smart.png';
import { theme } from '../styles/theme';
import { Spinner } from '../components/ui';
import { cognitoAuth } from '../services/cognito';

const API_URL = process.env.REACT_APP_API_URL;

export default function OnboardingWizard() {
  const { t } = useTranslation('wizard');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // steps: 0=verify-email 1=senha 2=agente 3=chat
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 0 — verificação de email
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyCode, setVerifyCode]   = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [codeSent, setCodeSent]       = useState(false);

  // Step 1 — conta
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');

  // Step 2 — agente
  const [marketplaceAgents, setMarketplaceAgents] = useState([]);
  const [selectedAgent, setSelectedAgent]         = useState(null);

  // Step 3 — chat
  const [tenantId, setTenantId]             = useState(null);
  const [agentInstanceId, setAgentInstanceId] = useState(null);
  const [messages, setMessages]             = useState([]);
  const [chatInput, setChatInput]           = useState('');
  const [chatSending, setChatSending]       = useState(false);
  const [lastPoll, setLastPoll]             = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef        = useRef(null);

  const referralRef = searchParams.get('ref') || '';
  const googleEmail = searchParams.get('email') || '';
  const googleName  = searchParams.get('name')  || '';

  // Google: pula verificação e criação de conta
  useEffect(() => {
    if (googleEmail) {
      setVerifyEmail(googleEmail);
      setVerifyToken('google-verified');
      setEmail(googleEmail);
      setStep(2);
    }
  }, []); // eslint-disable-line

  // Já logado via Google OAuth
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.tenantId) {
      setTenantId(user.tenantId);
      setStep(2);
    }
  }, []); // eslint-disable-line

  // Carrega agentes disponíveis para individual_free via endpoint público
  useEffect(() => {
    fetch(`${API_URL}/public/agents?tier=individual_free`)
      .then(r => r.json())
      .then(d => setMarketplaceAgents(d.agents || []))
      .catch(() => {});
  }, []);

  const getAgentIcon = (agentId) => {
    const found = marketplaceAgents.find(a => a.agentId === agentId);
    return found?.icon || '🤖';
  };

  // ── Step 0 → verificar email ─────────────────────────────────────────────
  const handleSendCode = async () => {
    if (!verifyEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(verifyEmail)) {
      setError(t('errorEmail', { defaultValue: 'Email inválido' })); return;
    }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/send-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verifyEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setCodeSent(true);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleVerifyCode = async () => {
    if (verifyCode.length < 6) { setError(t('errorCode', { defaultValue: 'Digite o código de 6 dígitos' })); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/verify-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verifyEmail, code: verifyCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setVerifyToken(data.verifyToken);
      setEmail(verifyEmail);
      setStep(1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── Step 1 → criar conta ──────────────────────────────────────────────────
  const handleCreateAccount = async () => {
    if (!password) { setError(t('errorFields')); return; }
    if (password.length < 8) { setError(t('errorPassword')); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/onboarding/quick-setup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, emailVerifyToken: verifyToken, referralRef })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || t('errorGeneric'));
      setTenantId(data.tenantId);
      const loginResult = await cognitoAuth.signIn(email, password);
      if (loginResult.success) localStorage.setItem('user', JSON.stringify(loginResult.user));
      setStep(2);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── Step 2 → instalar agente ──────────────────────────────────────────────
  const handleInstallAgent = async () => {
    setError(''); setLoading(true);
    try {
      const token = sessionStorage.getItem('idToken');
      const tid   = tenantId;
      const instanceName = selectedAgent.name || selectedAgent.agentId;

      const installRes = await fetch(`${API_URL}/marketplace/tenants/${tid}/agents/install`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          agentId:      selectedAgent.agentId,
          instanceName,
          tone:         'casual'
        })
      });
      const installData = await installRes.json();
      if (!installRes.ok) throw new Error(installData.message || t('errorGeneric'));

      const instanceId = installData.agent?.instanceId;
      if (!instanceId) throw new Error(t('errorAgentId', { defaultValue: 'Falha ao obter ID do agente instalado' }));

      // Garante que existe conector internal_chat para este agente
      let connectorRes = await fetch(`${API_URL}/connectors/${tid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let connData = await connectorRes.json();
      let conn = (connData.connectors || []).find(c => c.type === 'internal_chat' && c.enabled && c.agentInstanceId === instanceId);

      if (!conn) {
        // Cria o conector internal_chat para este agente
        await fetch(`${API_URL}/connectors/${tid}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            type: 'internal_chat',
            name: 'Chat',
            agentInstanceId: instanceId,
            enabled: true
          })
        });
        // Rebusca para pegar o conector criado
        connectorRes = await fetch(`${API_URL}/connectors/${tid}`, { headers: { Authorization: `Bearer ${token}` } });
        connData = await connectorRes.json();
        conn = (connData.connectors || []).find(c => c.type === 'internal_chat' && c.enabled && c.agentInstanceId === instanceId);
      }

      setAgentInstanceId(conn?.agentInstanceId || instanceId);

      const agentDisplayName = selectedAgent.name || selectedAgent.agentId;
      setMessages([{ from: 'agent', text: t('agentGreeting', { name: agentDisplayName }), time: fmtTime() }]);
      setStep(3);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── Chat polling ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== 3 || !tenantId || !agentInstanceId) return;
    pollRef.current = setInterval(doPoll, 3000);
    return () => clearInterval(pollRef.current);
  }, [step, tenantId, agentInstanceId, lastPoll]); // eslint-disable-line

  const doPoll = async () => {
    try {
      const token = sessionStorage.getItem('idToken');
      const res = await fetch(
        `${API_URL}/internal-chat/${tenantId}/poll${lastPoll ? `?since=${lastPoll}` : ''}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await res.json();
      if (data.agentReply) {
        setMessages(prev => [...prev, { from: 'agent', text: data.agentReply.message, time: fmtTime(data.agentReply.createdAt) }]);
        setLastPoll(data.agentReply.createdAt);
      }
    } catch { /* silencioso */ }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendChat = async () => {
    if (!chatInput.trim() || chatSending) return;
    const text = chatInput.trim();
    setChatInput('');
    setMessages(prev => [...prev, { from: 'me', text, time: fmtTime() }]);
    setChatSending(true);
    try {
      const token = sessionStorage.getItem('idToken');
      await fetch(`${API_URL}/internal-chat/${tenantId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ recipientId: agentInstanceId, recipientType: 'agent', message: text })
      });
    } catch { /* silencioso */ }
    finally { setChatSending(false); }
  };

  // Steps: 0=email-verify 1=senha 2=agente 3=chat
  // Google pula 0 e 1, começa no 2
  const STEPS_LABELS = googleEmail
    ? [t('stepAgent'), t('stepChat')]
    : [t('stepVerify', { defaultValue: 'Email' }), t('stepAccount'), t('stepAgent'), t('stepChat')];

  return (
    <Page>
      <Glow />
      <Container>
        <LogoImg src={logoColor} alt="Libben Agents" />

        <Card>
          {step < 4 && (
            <StepBar>
              {STEPS_LABELS.map((label, i) => (
                <React.Fragment key={i}>
                  <StepItem>
                    <StepDot $done={step > i} $active={step === i}>
                      {step > i ? <Check size={11} /> : i + 1}
                    </StepDot>
                    <StepLabel $active={step === i}>{label}</StepLabel>
                  </StepItem>
                  {i < STEPS_LABELS.length - 1 && <StepLine $done={step > i} />}
                </React.Fragment>
              ))}
            </StepBar>
          )}

          {/* ── Step 0: Verificação de email ── */}
          {step === 0 && (
            <StepContent>
              <StepTitle>{t('step0VerifyTitle', { defaultValue: 'Verificar email' })}</StepTitle>
              <StepSub>{t('step0VerifySub', { defaultValue: 'Vamos confirmar seu email antes de criar sua conta.' })}</StepSub>
              <Field>
                <FLabel>{t('email')}</FLabel>
                <FInput type="email" value={verifyEmail} onChange={e => setVerifyEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')} disabled={codeSent} autoFocus={!codeSent} />
              </Field>
              {codeSent && (
                <Field>
                  <FLabel>{t('verifyCode', { defaultValue: 'Código recebido por email' })}</FLabel>
                  <FInput value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" maxLength={6} autoFocus
                    style={{ textAlign: 'center', fontSize: '1.4rem', letterSpacing: '0.3em' }}
                    onKeyDown={e => e.key === 'Enter' && handleVerifyCode()} />
                </Field>
              )}
              {error && <ErrorBox>{error}</ErrorBox>}
              {!codeSent
                ? <PrimaryBtn onClick={handleSendCode} disabled={loading}>
                    {loading ? <><Spinner size="14px" /> {t('sending', { defaultValue: 'Enviando...' })}</> : t('sendCode', { defaultValue: 'Enviar código' })}
                  </PrimaryBtn>
                : <PrimaryBtn onClick={handleVerifyCode} disabled={loading || verifyCode.length < 6}>
                    {loading ? <><Spinner size="14px" /> {t('verifying', { defaultValue: 'Verificando...' })}</> : t('confirmCode', { defaultValue: 'Confirmar código' })}
                  </PrimaryBtn>
              }
            </StepContent>
          )}

          {/* ── Step 1: Criar senha ── */}
          {step === 1 && (
            <StepContent>
              <StepTitle>{t('step0Title')}</StepTitle>
              <StepSub>{t('step0Sub')}</StepSub>
              <Field>
                <FLabel>{t('email')}</FLabel>
                <FInput type="email" value={email} disabled />
              </Field>
              <Field>
                <FLabel>{t('password')}</FLabel>
                <FInput type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder={t('passwordPlaceholder')} autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleCreateAccount()} />
              </Field>
              {error && <ErrorBox>{error}</ErrorBox>}
              <PrimaryBtn onClick={handleCreateAccount} disabled={loading}>
                {loading ? <><Spinner size="14px" /> {t('creating')}</> : t('step0Cta')}
              </PrimaryBtn>
            </StepContent>
          )}

          {/* ── Step 2: Escolha do agente ── */}
          {step === 2 && (
            <StepContent>
              <StepTitle>{t('step1Title')}</StepTitle>
              <StepSub>{t('step1Sub')}</StepSub>
              {marketplaceAgents.length === 0 && (
                <div style={{ textAlign: 'center', color: theme.colors.gray400, padding: '24px 0' }}>
                  {t('loading', { ns: 'common' })}
                </div>
              )}
              <AgentGrid>
                {marketplaceAgents.map(a => (
                  <AgentCard key={a.agentId}
                    $selected={selectedAgent?.agentId === a.agentId}
                    onClick={() => setSelectedAgent(a)}>
                    <AgentIcon>{getAgentIcon(a.agentId)}</AgentIcon>
                    <AgentName>{a.name || a.agentId}</AgentName>
                    <AgentDesc>{a.description || ''}</AgentDesc>
                    {selectedAgent?.agentId === a.agentId && <SelectedCheck><Check size={12} /></SelectedCheck>}
                  </AgentCard>
                ))}
              </AgentGrid>
              {error && <ErrorBox>{error}</ErrorBox>}
              <PrimaryBtn onClick={() => { if (!selectedAgent) { setError(t('errorSelectAgent')); return; } setError(''); handleInstallAgent(); }}
                disabled={!selectedAgent || loading}>
                {loading ? <><Spinner size="14px" /> {t('creating')}</> : t('step2Cta')}
              </PrimaryBtn>
            </StepContent>
          )}

          {/* ── Step 3: Teste ao vivo ── */}
          {step === 3 && (
            <StepContent>
              <StepTitle>{t('step3Title', { name: selectedAgent?.name || '' })}</StepTitle>
              <StepSub>{t('step3Sub')}</StepSub>
              <ChatBox>
                <ChatMessages>
                  {messages.map((msg, i) => (
                    <ChatMsg key={i} $from={msg.from}>
                      <ChatBubble $from={msg.from}>{msg.text}</ChatBubble>
                      <ChatTime>{msg.time}</ChatTime>
                    </ChatMsg>
                  ))}
                  <div ref={messagesEndRef} />
                </ChatMessages>
                <ChatInputRow>
                  <ChatInput
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
                    placeholder={t('chatPlaceholder')}
                    disabled={chatSending}
                  />
                  <SendBtn aria-label={t('common:send')} onClick={sendChat} disabled={chatSending || !chatInput.trim()}>
                    {chatSending ? <Spinner size="14px" /> : <Send size={14} />}
                  </SendBtn>
                </ChatInputRow>
              </ChatBox>
              <PrimaryBtn onClick={() => navigate(`/dashboard/${tenantId}`)} style={{ marginTop: theme.spacing[4] }}>
                {t('goToDashboard')}
              </PrimaryBtn>
            </StepContent>
          )}
        </Card>

        <FooterNote>
          {t('alreadyHaveAccount')} <FooterLink onClick={() => navigate('/login')}>{t('signIn')}</FooterLink>
          {' · '}
          <FooterLink onClick={() => navigate('/register')}>{t('wantBusiness')}</FooterLink>
        </FooterNote>
      </Container>
    </Page>
  );
}

function fmtTime(iso) {
  return new Date(iso || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ── Styled ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh; background: ${theme.colors.gray900};
  display: flex; align-items: center; justify-content: center;
  padding: ${theme.spacing[6]} ${theme.spacing[4]}; position: relative; overflow: hidden;
`;
const Glow = styled.div`
  position: absolute; width: 600px; height: 600px; border-radius: 50%;
  background: radial-gradient(circle, ${theme.colors.primary}22 0%, transparent 70%);
  top: -150px; left: 50%; transform: translateX(-50%); pointer-events: none;
`;
const Container = styled.div`
  width: 100%; max-width: 480px;
  display: flex; flex-direction: column; align-items: center; gap: ${theme.spacing[5]};
`;
const LogoImg = styled.img`height: 40px; width: auto; object-fit: contain;`;
const Card = styled.div`
  width: 100%; background: ${theme.colors.white};
  border-radius: ${theme.radii['2xl']}; padding: ${theme.spacing[8]};
  box-shadow: ${theme.shadows.xl};
`;

const StepBar = styled.div`display: flex; align-items: center; margin-bottom: ${theme.spacing[7]};`;
const StepItem = styled.div`display: flex; flex-direction: column; align-items: center; gap: 4px;`;
const StepDot = styled.div`
  width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 600;
  background: ${p => p.$done ? theme.colors.success : p.$active ? theme.colors.primary : theme.colors.gray200};
  color: ${p => (p.$done || p.$active) ? '#fff' : theme.colors.textMuted};
`;
const StepLabel = styled.span`
  font-size: 10px; color: ${p => p.$active ? theme.colors.primary : theme.colors.textMuted};
  font-weight: ${p => p.$active ? 600 : 400}; white-space: nowrap;
`;
const StepLine = styled.div`
  flex: 1; height: 2px; margin-bottom: 14px;
  background: ${p => p.$done ? theme.colors.success : theme.colors.gray200};
`;

const StepContent = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[4]};`;
const StepTitle = styled.h2`font-size: ${theme.typography.xl}; font-weight: ${theme.typography.bold}; color: ${theme.colors.textPrimary}; margin: 0;`;
const StepSub = styled.p`font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted}; margin: 0;`;

const Field = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[1]};`;
const FLabel = styled.label`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const FInput = styled.input`
  padding: ${theme.spacing[3]}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm};
  font-family: inherit; outline: none;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
  &::placeholder { color: ${theme.colors.textMuted}; }
`;

const AgentGrid = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: ${theme.spacing[3]};`;
const AgentCard = styled.div`
  position: relative; border: 2px solid ${p => p.$selected ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.xl}; padding: ${theme.spacing[3]};
  cursor: pointer; background: ${p => p.$selected ? theme.colors.primaryLight : theme.colors.white};
  transition: ${theme.transitions.fast}; text-align: center;
  &:hover { border-color: ${theme.colors.primary}; }
`;
const AgentIcon = styled.div`font-size: 28px; margin-bottom: ${theme.spacing[1]};`;
const AgentName = styled.div`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const AgentDesc = styled.div`font-size: 10px; color: ${theme.colors.textMuted}; margin-top: 2px; line-height: 1.3;`;
const SelectedCheck = styled.div`
  position: absolute; top: 6px; right: 6px;
  width: 18px; height: 18px; border-radius: 50%;
  background: ${theme.colors.primary}; color: white;
  display: flex; align-items: center; justify-content: center;
`;

const ChatBox = styled.div`
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.xl};
  overflow: hidden; display: flex; flex-direction: column; height: 280px;
`;
const ChatMessages = styled.div`flex: 1; overflow-y: auto; padding: ${theme.spacing[3]}; display: flex; flex-direction: column; gap: ${theme.spacing[2]};`;
const ChatMsg = styled.div`display: flex; flex-direction: column; align-items: ${p => p.$from === 'me' ? 'flex-end' : 'flex-start'};`;
const ChatBubble = styled.div`
  max-width: 80%; padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.radii.lg};
  background: ${p => p.$from === 'me' ? theme.colors.primary : theme.colors.gray100};
  color: ${p => p.$from === 'me' ? 'white' : theme.colors.textPrimary};
  font-size: ${theme.typography.sm}; line-height: 1.5;
`;
const ChatTime = styled.div`font-size: 10px; color: ${theme.colors.textMuted}; margin-top: 2px;`;
const ChatInputRow = styled.div`display: flex; gap: ${theme.spacing[2]}; padding: ${theme.spacing[2]}; border-top: 1px solid ${theme.colors.border};`;
const ChatInput = styled.input`
  flex: 1; padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; font-family: inherit; outline: none;
  &:focus { border-color: ${theme.colors.primary}; }
`;
const SendBtn = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.primary}; color: white;
  border: none; border-radius: ${theme.radii.md}; cursor: pointer;
  display: flex; align-items: center;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary}; color: white;
  border: none; border-radius: ${theme.radii.md};
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  cursor: pointer; transition: ${theme.transitions.fast};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; }
`;
const BackLink = styled.button`
  background: none; border: none; color: ${theme.colors.textMuted};
  font-size: ${theme.typography.sm}; cursor: pointer; align-self: center;
  &:hover { color: ${theme.colors.textPrimary}; }
`;
const ErrorBox = styled.div`
  padding: ${theme.spacing[3]}; background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.dangerBorder}; border-radius: ${theme.radii.md};
  color: ${theme.colors.danger}; font-size: ${theme.typography.sm};
`;
const FooterNote = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.gray500}; text-align: center;`;
const FooterLink = styled.span`color: ${theme.colors.primary}; cursor: pointer; &:hover { text-decoration: underline; }`;
